import {
  QueryHookOptions,
  QueryResult,
  SuspenseQueryHookOptions,
  UseSuspenseQueryResult,
} from '@apollo/client';

type QueryVariables = {
  filter?: string | null;
  pageSize?: number | null;
  pageToken?: string | null;
  orderBy?: string | null;
};

// Overload for regular hooks
export function withRequiredVariables<
  TData extends Record<string, unknown>,
  TRequiredVars extends Record<string, unknown>,
>(
  useQuery: (
    options: QueryHookOptions<TData, TRequiredVars> & {
      variables: TRequiredVars;
      skip?: boolean;
    },
  ) => QueryResult<TData, TRequiredVars>,
): (
  options?: QueryHookOptions<TData, QueryVariables & TRequiredVars>,
) => QueryResult<TData, QueryVariables & TRequiredVars>;

// Overload for suspense hooks
export function withRequiredVariables<
  TData extends Record<string, unknown>,
  TRequiredVars extends Record<string, unknown>,
>(
  useQuery: (
    options?: SuspenseQueryHookOptions<TData | undefined, TRequiredVars>,
  ) => UseSuspenseQueryResult<TData | undefined, TRequiredVars>,
): (
  options?: SuspenseQueryHookOptions<
    TData | undefined,
    QueryVariables & TRequiredVars
  >,
) => UseSuspenseQueryResult<TData | undefined, QueryVariables & TRequiredVars>;

// Implementation
export function withRequiredVariables<
  TData extends Record<string, unknown>,
  TRequiredVars extends Record<string, unknown>,
>(
  useQuery:
    | ((
        options: QueryHookOptions<TData, TRequiredVars> & {
          variables: TRequiredVars;
          skip?: boolean;
        },
      ) => QueryResult<TData, TRequiredVars>)
    | ((
        options?: SuspenseQueryHookOptions<TData | undefined, TRequiredVars>,
      ) => UseSuspenseQueryResult<TData | undefined, TRequiredVars>),
) {
  return (options?: Record<string, unknown>) => {
    return (useQuery as (options?: Record<string, unknown>) => unknown)({
      ...options,
      variables: options?.variables as Record<string, unknown> | undefined,
    });
  };
}
